import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import type { EpgChannel, EpgProgram } from '@jwp/ott-common/types/epg';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import { getModule } from '@jwp/ott-common/src/modules/container';
import EpgController from '@jwp/ott-common/src/controllers/EpgController';
import { createURL, liveChannelsURL, mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { programIsLive } from '@jwp/ott-common/src/utils/epg';

/**
 * Resolves the url for an EPG program click; `undefined` when the program is not navigable.
 */
export default function useEpgPlaybackUrl(playlist: PlaylistItem[], channels: EpgChannel[]) {
  const channelEntitlements = useEntitlement(playlist);
  const areChannelsEntitled = channelEntitlements.reduce((acc, { mediaid, isEntitled }) => {
    return { ...acc, [mediaid!]: isEntitled };
  }, {} as { [mediaid: string]: boolean });
  const epgController = getModule(EpgController);

  const getPlaybackUrl = (channelId: string, program: EpgProgram, fromBeginning: boolean, view: string, offset: number | undefined) => {
    const channelItem = playlist.find(({ mediaid }) => channelId === mediaid);
    const channel = channels.find(({ id }) => id === channelId);
    const isEntitled = channelItem && areChannelsEntitled[channelItem.mediaid];
    const navigation = channelItem && channel && epgController.getProgramNavigation(channelItem, program, channel);
    const feedId = playlist[0]?.feedid;

    if (!navigation || !feedId) return undefined;

    if (navigation.type === 'media') {
      return mediaURL({ id: navigation.programId, title: program.title });
    }

    if (!isEntitled && channelItem) {
      // paywall is handled on the media page
      const paywallMediaId = navigation.programId ?? channelItem.mediaid;

      return paywallMediaId ? mediaURL({ id: paywallMediaId, title: program.title }) : undefined;
    }

    return createURL(liveChannelsURL(feedId, channelId, true, playlist[0].title), {
      start: fromBeginning ? program.startTime : navigation.startAt,
      end: navigation.endAt,
      play: 1,
      program: !programIsLive(program) ? program.id : undefined,
      beginning: fromBeginning ? '1' : undefined,
      view,
      offset: offset || undefined,
    });
  };

  return getPlaybackUrl;
}
